import {
  Component,
  input,
  output,
  effect,
  OnInit,
  OnDestroy,
  AfterViewInit,
  OnChanges,
  SimpleChanges,
  ViewChild,
  ElementRef,
  NgZone,
  inject,
  ChangeDetectorRef,
  PLATFORM_ID,
  HostListener,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import type * as L from 'leaflet';
import {
  MerchantMapLocation,
  Opportunity,
} from '../../../services/dashboard-itinerant.service';
import { LogService } from '../../../services/log.service';

export interface MapEvent {
  id: string;
  name: string;
  lat: number;
  lng: number;
  location: string;
  eventType: string;
  status: string;
  date?: string;
}

@Component({
  selector: 'app-map-widget',
  imports: [FormsModule],
  templateUrl: './map-widget.component.html',
  styleUrls: ['./map-widget.component.css'],
})
export class MapWidgetComponent
  implements OnInit, AfterViewInit, OnDestroy, OnChanges
{
  private log = inject(LogService);
  @ViewChild('mapContainer', { static: false }) mapContainer!: ElementRef;

  /** Position du marchand (centre de la carte) */
  readonly merchantLocation = input<MerchantMapLocation | null>(null);

  /** Événements planifiés/actifs du marchand */
  readonly events = input<MapEvent[]>([]);

  /** Opportunités détectées */
  readonly opportunities = input<Opportunity[]>([]);

  /** IDs des opportunités déjà suivies (depuis le backend) */
  readonly initialFollowedIds = input<string[]>([]);

  /** Hauteur de la carte */
  readonly mapHeight = input<string>('560px');

  /** Override du nombre d'opportunités affiché dans la légende (depuis le parent) */
  readonly oppCountOverride = input<number | null>(null);

  get displayOppCount(): number {
    return this.oppCountOverride() !== null
      ? this.oppCountOverride()!
      : this.oppsInRadius;
  }

  /** Rayon d'action modifié par l'utilisateur */
  readonly radiusChanged = output<number>();

  /** Opportunité sélectionnée pour suivi */
  readonly opportunityFollowed = output<Opportunity>();

  // Leaflet
  private map!: L.Map;
  private radiusCircle!: L.Circle;
  private merchantMarker!: L.Marker;
  // Marqueurs avec distance pré-calculée (immuable pour le jeu de données courant)
  private eventMarkers: {
    marker: L.Marker;
    lat: number;
    lng: number;
    distM: number;
    eventsCount: number; // nombre réel d'événements représentés
  }[] = [];
  private oppMarkers: {
    marker: L.Marker;
    lat: number;
    lng: number;
    distM: number;
    level: string; // 'high' | 'medium' | 'low'
    opportunities: Opportunity[]; // Toutes les opportunités à cette position
  }[] = [];
  mapInitialized = false;

  private platformId = inject(PLATFORM_ID);
  private ngZone = inject(NgZone);
  private cdr = inject(ChangeDetectorRef);
  private router = inject(Router);
  private _L: typeof import('leaflet') | null = null;

  // UI
  isExpanded = false;
  isFullscreen = false;
  editingRadius = false;
  radiusValue: number = 15;
  mapZoomActive = false;
  activeLegendFilter:
    | 'merchant'
    | 'events'
    | 'opp-high'
    | 'opp-medium'
    | 'opp-low'
    | null = null;

  // Opportunités suivies (stockage local)
  private followedOpportunityIds = new Set<string>();

  // Effect to sync initialFollowedIds signal → followedOpportunityIds Set
  private _followedIdsEffect = effect(() => {
    const ids = this.initialFollowedIds();
    this.followedOpportunityIds.clear();
    if (ids && ids.length > 0) {
      ids.forEach((id) => this.followedOpportunityIds.add(id));
    }
  });

  // Toast notification
  toastVisible = false;
  toastMessage = '';

  // Légende stats
  get eventsInRadius(): number {
    if (!this.merchantLocation()) return this.events().length;
    return this.eventMarkers
      .filter((e) => e.distM <= this.radiusValue * 1000)
      .reduce((sum, e) => sum + e.eventsCount, 0);
  }

  get oppsInRadius(): number {
    if (!this.merchantLocation()) return this.opportunities().length;
    const radiusM = this.radiusValue * 1000;
    return this.oppMarkers
      .filter((m) => m.distM <= radiusM)
      .reduce((sum, m) => sum + m.opportunities.length, 0);
  }

  get oppsHighInRadius(): number {
    // Compter toutes les opportunités high dans le rayon (plusieurs opps par marqueur possible)
    const radiusM = this.radiusValue * 1000;
    return this.oppMarkers
      .filter((m) => m.distM <= radiusM && m.level === 'high')
      .reduce((sum, m) => sum + m.opportunities.length, 0);
  }

  get oppsMediumInRadius(): number {
    // Compter toutes les opportunités medium dans le rayon (plusieurs opps par marqueur possible)
    const radiusM = this.radiusValue * 1000;
    return this.oppMarkers
      .filter((m) => m.distM <= radiusM && m.level === 'medium')
      .reduce((sum, m) => sum + m.opportunities.length, 0);
  }

  get oppsLowInRadius(): number {
    // Compter toutes les opportunités low dans le rayon (plusieurs opps par marqueur possible)
    const radiusM = this.radiusValue * 1000;
    return this.oppMarkers
      .filter((m) => m.distM <= radiusM && m.level === 'low')
      .reduce((sum, m) => sum + m.opportunities.length, 0);
  }

  ngOnInit(): void {
    if (this.merchantLocation()) {
      this.radiusValue = this.merchantLocation()!.actionRadius;
    }
  }

  async ngAfterViewInit(): Promise<void> {
    if (isPlatformBrowser(this.platformId)) {
      const leaflet = await import('leaflet');
      // Interop CJS/ESM : cf. LocationPickerComponent.scheduleMapInit()
      // (bug constaté 2026-07-08) — `.default` peut porter le module réel.
      const withDefault = leaflet as unknown as { default?: typeof leaflet };
      this._L = withDefault.default ?? leaflet;
      this.ngZone.runOutsideAngular(() =>
        setTimeout(() => this.initMap(), 150),
      );
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.mapInitialized) return;

    if (
      changes['events'] ||
      changes['opportunities'] ||
      changes['initialFollowedIds']
    ) {
      this.updateMarkers();
    }
    if (changes['merchantLocation'] && this.merchantLocation()) {
      this.radiusValue = this.merchantLocation()!.actionRadius;
      this.updateMerchantPosition();
    }
  }

  // ═══ Map init ═══

  private initMap(): void {
    if (this.mapInitialized || !this.mapContainer) return;
    const L = this._L!;

    const center: [number, number] = this.merchantLocation()
      ? [this.merchantLocation()!.lat, this.merchantLocation()!.lng]
      : [43.6007, 1.4335]; // Toulouse par défaut

    try {
      this.map = L.map(this.mapContainer.nativeElement, {
        center,
        zoom: 12,
        zoomControl: false,
        attributionControl: false,
        scrollWheelZoom: false, // Désactivé par défaut
      });

      // Activer le zoom à la molette après clic sur la carte
      this.map.on('click', () => {
        this.map.scrollWheelZoom.enable();
        this.mapZoomActive = true;
        this.cdr.markForCheck();
      });

      // Désactiver le zoom à la molette quand la souris quitte la carte
      this.map.on('mouseout', () => {
        this.map.scrollWheelZoom.disable();
        this.mapZoomActive = false;
        this.cdr.markForCheck();
      });

      // Zoom control en bas à droite
      L.control.zoom({ position: 'bottomright' }).addTo(this.map);

      // Attribution discrète
      L.control
        .attribution({ position: 'bottomleft', prefix: '' })
        .addTo(this.map);

      // Tuiles CartoDB Positron (style clair et sobre)
      L.tileLayer(
        'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png',
        {
          attribution: '&copy; <a href="https://carto.com/">CARTO</a>',
          maxZoom: 19,
        },
      ).addTo(this.map);

      // Rayon d'action
      if (this.merchantLocation()) {
        this.radiusCircle = L.circle(center, {
          radius: this.radiusValue * 1000,
          color: '#6366f1',
          fillColor: '#6366f1',
          fillOpacity: 0.06,
          weight: 2,
          dashArray: '6 4',
        }).addTo(this.map);
      }

      // Marqueur marchand (bleu)
      if (this.merchantLocation()) {
        this.merchantMarker = L.marker(center, {
          icon: this.createDivIcon('🏠', 'bg-blue-600'),
          zIndexOffset: 1000,
        })
          .bindPopup(this.createMerchantPopup())
          .addTo(this.map);
      }

      this.mapInitialized = true;
      this.updateMarkers();

      // Ajuster le zoom pour contenir le rayon
      if (this.merchantLocation() && this.radiusCircle) {
        this.map.fitBounds(this.radiusCircle.getBounds(), {
          padding: [20, 20],
        });
      }

      // Écouter les clics sur le bouton "Suivre" dans les popups
      this.setupFollowOpportunityListener();

      this.ngZone.runOutsideAngular(() =>
        setTimeout(() => this.map?.invalidateSize(), 200),
      );
    } catch (error) {
      this.log.error('Erreur initialisation carte:', error);
    }
  }

  /** Écoute l'événement custom émis par le bouton "Suivre" dans les popups */
  private followOpportunityHandler = (e: Event) => {
    const customEvent = e as CustomEvent;
    const oppId = customEvent.detail;
    const opp = this.opportunities().find((o) => o.id === oppId);
    if (opp && !this.followedOpportunityIds.has(oppId)) {
      // Ajouter à la liste des opportunités suivies localement
      this.followedOpportunityIds.add(oppId);

      // Afficher toast
      this.showToast(`✅ "${opp.name}" ajoutée aux événements planifiés`);

      // Mettre à jour les marqueurs pour refléter le changement
      this.updateMarkers();

      // Émettre vers le parent pour sync backend (ignore l'erreur si déjà suivi)
      this.opportunityFollowed.emit(opp);
      this.cdr.markForCheck();
    }
  };

  private showToast(message: string): void {
    this.toastMessage = message;
    this.toastVisible = true;
    this.cdr.markForCheck();
    setTimeout(() => {
      this.toastVisible = false;
      this.cdr.markForCheck();
    }, 3000);
  }

  private setupFollowOpportunityListener(): void {
    window.addEventListener('followOpportunity', this.followOpportunityHandler);
  }

  ngOnDestroy(): void {
    window.removeEventListener(
      'followOpportunity',
      this.followOpportunityHandler,
    );
    if (this.map) {
      this.mapInitialized = false;
      this.map.off();
      this.map.remove();
    }
  }

  private updateMarkers(): void {
    if (!this.mapInitialized) return;
    const L = this._L!;

    // Supprimer les anciens marqueurs
    this.eventMarkers.forEach((e) => e.marker.remove());
    this.oppMarkers.forEach((o) => o.marker.remove());
    this.eventMarkers = [];
    this.oppMarkers = [];

    // ─── Regrouper événements ET opportunités par coordonnée ───
    const coordsMap = new Map<
      string,
      { events: MapEvent[]; opps: Opportunity[] }
    >();

    this.events().forEach((event) => {
      if (!event.lat || !event.lng) return;
      const key = `${event.lat.toFixed(4)},${event.lng.toFixed(4)}`;
      if (!coordsMap.has(key)) coordsMap.set(key, { events: [], opps: [] });
      coordsMap.get(key)!.events.push(event);
    });

    this.opportunities().forEach((opp) => {
      if (!opp.lat || !opp.lng) return;
      const key = `${opp.lat.toFixed(4)},${opp.lng.toFixed(4)}`;
      if (!coordsMap.has(key)) coordsMap.set(key, { events: [], opps: [] });
      coordsMap.get(key)!.opps.push(opp);
    });

    // ─── Créer un marqueur unique par position ───
    coordsMap.forEach(({ events, opps }, coordKey) => {
      const [latStr, lngStr] = coordKey.split(',');
      const lat = parseFloat(latStr);
      const lng = parseFloat(lngStr);
      const distM = this.calcDistM(lat, lng);

      if (events.length > 0 && opps.length === 0) {
        // ── Événements seuls ──
        if (events.length === 1) {
          const event = events[0];
          const marker = L.marker([event.lat, event.lng], {
            icon: this.createDivIcon(
              this.getEventEmoji(event.eventType),
              event.status === 'active'
                ? 'bg-green-500 animate-pulse'
                : 'bg-purple-600',
            ),
          })
            .bindPopup(this.createEventPopup(event), {
              minWidth: 320,
              maxWidth: 320,
            })
            .addTo(this.map);
          this.eventMarkers.push({
            marker,
            lat: event.lat,
            lng: event.lng,
            distM,
            eventsCount: 1,
          });
        } else {
          // Plusieurs événements au même endroit → marqueur groupé
          const hasActive = events.some((e) => e.status === 'active');
          const icon = this.createDivIconWithBadge(
            '📅',
            hasActive ? 'bg-green-500 animate-pulse' : 'bg-purple-600',
            events.length,
          );
          const marker = L.marker([lat, lng], { icon })
            .bindPopup(this.createGroupedEventPopup(events), {
              minWidth: 320,
              maxWidth: 320,
            })
            .addTo(this.map);
          this.eventMarkers.push({
            marker,
            lat,
            lng,
            distM,
            eventsCount: events.length,
          });
        }
      } else if (opps.length > 0 && events.length === 0) {
        // ── Opportunités seules ──
        const allFollowed = opps.every((opp) =>
          this.followedOpportunityIds.has(opp.id),
        );
        const levels = opps.map((o) => o.profitabilityLevel || 'low');
        const level = levels.includes('high')
          ? 'high'
          : levels.includes('medium')
            ? 'medium'
            : 'low';

        let bgColor = 'bg-orange-500';
        let emoji = '💡';
        if (allFollowed) {
          bgColor = 'bg-purple-600';
          emoji = '📅';
        } else if (level === 'high') bgColor = 'bg-green-500';
        else if (level === 'medium') bgColor = 'bg-yellow-400';

        const icon =
          opps.length > 1
            ? this.createDivIconWithBadge(emoji, bgColor, opps.length)
            : this.createDivIcon(emoji, bgColor);

        const popupContent = allFollowed
          ? this.createFollowedEventsPopup(opps)
          : this.createGroupedOppPopup(opps);

        const marker = L.marker([lat, lng], { icon })
          .bindPopup(popupContent, { minWidth: 320, maxWidth: 320 })
          .addTo(this.map);

        this.oppMarkers.push({
          marker,
          lat,
          lng,
          distM,
          level,
          opportunities: opps,
        });
      } else {
        // ── Mixte : événements + opportunités à la même position ──
        const icon = this.createMixedDivIcon(events.length, opps.length);
        const marker = L.marker([lat, lng], { icon })
          .bindPopup(this.createMixedPopup(events, opps), {
            minWidth: 320,
            maxWidth: 320,
          })
          .addTo(this.map);
        // Uniquement dans eventMarkers (les événements ont priorité)
        this.eventMarkers.push({
          marker,
          lat,
          lng,
          distM,
          eventsCount: events.length,
        });
      }
    });

    this.applyRadiusFilter();
  }

  /** Popup liste pour plusieurs événements au même endroit */
  private createGroupedEventPopup(events: MapEvent[]): string {
    const location = events[0].location || '';
    const statusLabel: Record<string, string> = {
      planned: '📅 Planifié',
      active: '🟢 En cours',
      completed: '✅ Terminé',
      analyzed: '📊 Analysé',
    };
    const statusColor: Record<string, string> = {
      planned: '#9333ea',
      active: '#16a34a',
      completed: '#6b7280',
      analyzed: '#2563eb',
    };

    const items = events
      .map(
        (event) => `
      <div style="border:1px solid #e9d5ff;border-radius:6px;padding:8px;margin-bottom:8px;background:#faf5ff;cursor:pointer"
           onclick="(function(){var el=document.getElementById('gev-${event.id}');if(el){el.style.transform='translateX(0)';el.style.opacity='1';}})()"
           onmouseover="this.style.background='#f3e8ff'" onmouseout="this.style.background='#faf5ff'">
        <div style="display:flex;align-items:start;gap:8px">
          <span style="font-size:16px">${this.getEventEmoji(event.eventType)}</span>
          <div style="flex:1">
            <p style="font-weight:600;margin:0;font-size:13px;color:#111827">${event.name}</p>
            <p style="margin:2px 0 0;font-size:11px;color:${statusColor[event.status] || '#6b7280'};font-weight:600">${statusLabel[event.status] || event.status}</p>
          </div>
          <span style="font-size:12px;color:#9ca3af">›</span>
        </div>
      </div>`,
      )
      .join('');

    const panels = events
      .map(
        (event) => `
      <div id="gev-${event.id}" data-panel="true" style="position:absolute;top:0;left:0;width:100%;background:white;transform:translateX(100%);opacity:0;transition:all 0.3s ease;border-radius:12px;">
        ${this.createEventPopup(event)}
        <div style="padding:0 12px 12px">
          <button onclick="(function(btn){var p=btn.closest('[data-panel]');if(p){p.style.transform='translateX(100%)';p.style.opacity='0';}})(this)"
                  style="width:100%;padding:8px;border:1px solid #e5e7eb;border-radius:6px;background:#f9fafb;cursor:pointer;font-size:12px;color:#374151;font-family:system-ui">← Retour à la liste</button>
        </div>
      </div>`,
      )
      .join('');

    return `
      <div style="font-family:system-ui;width:320px;position:relative;overflow:visible;box-sizing:border-box;">
        <div>
          <div style="background:#f9fafb;border-bottom:1px solid #e5e7eb;padding:10px;margin:-10px -10px 10px">
            <p style="font-weight:700;margin:0;font-size:14px;color:#111827">📅 ${events.length} événements planifiés</p>
            <p style="font-size:12px;color:#6b7280;margin:4px 0 0">📍 ${location}</p>
          </div>
          <div style="${events.length > 3 ? 'max-height:240px;overflow-y:auto;padding-right:2px;' : ''}">
            ${items}
          </div>
          <p style="font-size:10px;color:#9ca3af;margin:8px 0 0;text-align:center">💡 Cliquez pour voir les détails</p>
        </div>
        ${panels}
      </div>`;
  }

  /** Popup mixte : événements planifiés + opportunités restantes à la même position */
  private createMixedPopup(events: MapEvent[], opps: Opportunity[]): string {
    const location =
      events[0]?.location || opps[0]?.fullAddress || opps[0]?.location || '';
    const totalCount = events.length + opps.length;

    const eventItems = events
      .map(
        (event) => `
      <div style="border:1px solid #e9d5ff;border-radius:6px;padding:8px;margin-bottom:8px;background:#faf5ff;cursor:pointer"
           onclick="(function(){var el=document.getElementById('mx-ev-${event.id}');if(el){el.style.transform='translateX(0)';el.style.opacity='1';}})()"
           onmouseover="this.style.background='#f3e8ff'" onmouseout="this.style.background='#faf5ff'">
        <div style="display:flex;align-items:start;gap:8px">
          <span style="font-size:16px">${this.getEventEmoji(event.eventType)}</span>
          <div style="flex:1">
            <p style="font-weight:600;margin:0;font-size:13px;color:#111827">${event.name}</p>
            <p style="margin:2px 0 0;font-size:11px;color:#6b7280">📍 ${event.location}</p>
            <p style="margin:2px 0 0;font-size:11px;color:#9333ea;font-weight:600">✅ Planifié</p>
          </div>
        </div>
      </div>`,
      )
      .join('');

    const oppItems = opps
      .map((opp) => {
        const levelColors: Record<string, { dot: string; text: string }> = {
          high: { dot: '#22c55e', text: '#16a34a' },
          medium: { dot: '#facc15', text: '#ca8a04' },
          low: { dot: '#f97316', text: '#ea580c' },
        };
        const colors =
          levelColors[opp.profitabilityLevel] || levelColors['low'];
        return `
      <div style="border:1px solid #e5e7eb;border-radius:6px;padding:8px;margin-bottom:8px;background:white;cursor:pointer"
           onclick="(function(){var el=document.getElementById('mx-op-${opp.id}');if(el){el.style.transform='translateX(0)';el.style.opacity='1';}})()"
           onmouseover="this.style.background='#f9fafb'" onmouseout="this.style.background='white'">
        <div style="display:flex;align-items:start;gap:8px;margin-bottom:4px">
          <span style="width:8px;height:8px;border-radius:50%;background:${colors.dot};flex-shrink:0;margin-top:4px"></span>
          <div style="flex:1">
            <p style="font-weight:600;margin:0;font-size:13px;color:#111827">${opp.name}</p>
            <p style="margin:2px 0 0;font-size:11px;color:#6b7280">📅 ${opp.dateFormatted || opp.date}</p>
          </div>
        </div>
        <div style="display:flex;gap:8px;font-size:11px;color:#6b7280">
          <span>👥 ${opp.expectedAttendance || '?'}</span>
          <span style="color:${colors.text};font-weight:600">💰 ${opp.estimatedRevenue.low}–${opp.estimatedRevenue.high}€</span>
        </div>
      </div>`;
      })
      .join('');

    const eventPanels = events
      .map(
        (event) => `
      <div id="mx-ev-${event.id}" data-panel="true" style="position:absolute;top:0;left:0;width:100%;background:white;transform:translateX(100%);opacity:0;transition:all 0.3s ease;border-radius:12px;">
        ${this.createEventPopup(event)}
        <div style="padding:0 12px 12px">
          <button onclick="(function(btn){var p=btn.closest('[data-panel]');if(p){p.style.transform='translateX(100%)';p.style.opacity='0';}})(this)"
                  style="width:100%;padding:8px;border:1px solid #e5e7eb;border-radius:6px;background:#f9fafb;cursor:pointer;font-size:12px;color:#374151;font-family:system-ui">← Retour à la liste</button>
        </div>
      </div>`,
      )
      .join('');

    const oppPanels = opps
      .map(
        (opp) => `
      <div id="mx-op-${opp.id}" data-panel="true" style="position:absolute;top:0;left:0;width:100%;background:white;transform:translateX(100%);opacity:0;transition:all 0.3s ease;border-radius:12px;">
        ${this.createOppPopup(opp, true)}
      </div>`,
      )
      .join('');

    return `
      <div style="font-family:system-ui;width:320px;position:relative;overflow:visible;box-sizing:border-box;">
        <div>
          <div style="background:#f9fafb;border-bottom:1px solid #e5e7eb;padding:10px;margin:-10px -10px 10px">
            <p style="font-weight:700;margin:0;font-size:14px;color:#111827">📍 ${totalCount} élément${totalCount > 1 ? 's' : ''}</p>
            <p style="font-size:12px;color:#6b7280;margin:4px 0 0">${location}</p>
          </div>
          <div style="${totalCount > 3 ? 'max-height:240px;overflow-y:auto;padding-right:2px;' : ''}">
            ${eventItems}
            ${oppItems}
          </div>
          <p style="font-size:10px;color:#9ca3af;margin:8px 0 0;text-align:center">💡 Cliquez pour voir les détails</p>
        </div>
        ${eventPanels}
        ${oppPanels}
      </div>`;
  }

  /**
   * Calcule la distance en mètres entre le marchand et un point.
   * Stockée à la création du marqueur — ne change pas tant que merchantLocation ne change pas.
   */
  private calcDistM(lat: number, lng: number): number {
    if (!this.merchantLocation()) return 0;
    const L = this._L!;
    return L.latLng(
      this.merchantLocation()!.lat,
      this.merchantLocation()!.lng,
    ).distanceTo(L.latLng(lat, lng));
  }

  /**
   * Masque/affiche chaque marqueur en comparant sa distance pré-stockée au rayon.
   * Appliqué synchroniquement — pas de RAF, pas de race condition possible.
   * Respecte également le filtre de légende actif (activeLegendFilter).
   */
  private applyRadiusFilter(radius = this.radiusValue): void {
    const radiusM = radius * 1000;
    const apply = (entry: { marker: L.Marker; distM: number }) => {
      const visible = entry.distM <= radiusM;
      entry.marker.setOpacity(visible ? 1 : 0);
      const el = entry.marker.getElement();
      if (el) {
        el.style.visibility = visible ? 'visible' : 'hidden';
        el.style.pointerEvents = visible ? '' : 'none';
      }
    };

    // Appliquer filtre selon activeLegendFilter
    if (this.activeLegendFilter === 'merchant') {
      this.eventMarkers.forEach((e) => {
        e.marker.setOpacity(0);
        const el = e.marker.getElement();
        if (el) {
          el.style.visibility = 'hidden';
          el.style.pointerEvents = 'none';
        }
      });
      this.oppMarkers.forEach((o) => {
        o.marker.setOpacity(0);
        const el = o.marker.getElement();
        if (el) {
          el.style.visibility = 'hidden';
          el.style.pointerEvents = 'none';
        }
      });
      if (this.merchantMarker) {
        this.merchantMarker.setOpacity(1);
        const el = this.merchantMarker.getElement();
        if (el) {
          el.style.visibility = 'visible';
          el.style.pointerEvents = '';
        }
      }
    } else if (this.activeLegendFilter === 'events') {
      this.eventMarkers.forEach(apply);
      this.oppMarkers.forEach((o) => {
        o.marker.setOpacity(0);
        const el = o.marker.getElement();
        if (el) {
          el.style.visibility = 'hidden';
          el.style.pointerEvents = 'none';
        }
      });
      if (this.merchantMarker) {
        this.merchantMarker.setOpacity(0.3);
        const el = this.merchantMarker.getElement();
        if (el) {
          el.style.visibility = 'visible';
          el.style.pointerEvents = '';
        }
      }
    } else if (this.activeLegendFilter === 'opp-high') {
      // Afficher uniquement opportunités "high" dans le rayon
      this.oppMarkers.forEach((o) => {
        const visible = o.distM <= radiusM && o.level === 'high';
        o.marker.setOpacity(visible ? 1 : 0);
        const el = o.marker.getElement();
        if (el) {
          el.style.visibility = visible ? 'visible' : 'hidden';
          el.style.pointerEvents = visible ? '' : 'none';
        }
      });
      this.eventMarkers.forEach((e) => {
        e.marker.setOpacity(0);
        const el = e.marker.getElement();
        if (el) {
          el.style.visibility = 'hidden';
          el.style.pointerEvents = 'none';
        }
      });
      if (this.merchantMarker) {
        this.merchantMarker.setOpacity(0.3);
        const el = this.merchantMarker.getElement();
        if (el) {
          el.style.visibility = 'visible';
          el.style.pointerEvents = '';
        }
      }
    } else if (this.activeLegendFilter === 'opp-medium') {
      // Afficher uniquement opportunités "medium" dans le rayon
      let visibleCount = 0;
      const coordsMap = new Map<string, any[]>();
      this.oppMarkers.forEach((o) => {
        const visible = o.distM <= radiusM && o.level === 'medium';
        if (visible) {
          visibleCount++;
          const key = `${o.lat.toFixed(6)},${o.lng.toFixed(6)}`;
          if (!coordsMap.has(key)) coordsMap.set(key, []);
          coordsMap.get(key)!.push(o);
        }
        o.marker.setOpacity(visible ? 1 : 0);
        const el = o.marker.getElement();
        if (el) {
          el.style.visibility = visible ? 'visible' : 'hidden';
          el.style.pointerEvents = visible ? '' : 'none';
        }
      });
      // Note: coordsMap ici est pour la détection visibilité, pas pour log doublons (déjà gérés à la création)
      this.eventMarkers.forEach((e) => {
        e.marker.setOpacity(0);
        const el = e.marker.getElement();
        if (el) {
          el.style.visibility = 'hidden';
          el.style.pointerEvents = 'none';
        }
      });
      if (this.merchantMarker) {
        this.merchantMarker.setOpacity(0.3);
        const el = this.merchantMarker.getElement();
        if (el) {
          el.style.visibility = 'visible';
          el.style.pointerEvents = '';
        }
      }
    } else if (this.activeLegendFilter === 'opp-low') {
      // Afficher uniquement opportunités "low" dans le rayon
      this.oppMarkers.forEach((o) => {
        const visible = o.distM <= radiusM && o.level === 'low';
        o.marker.setOpacity(visible ? 1 : 0);
        const el = o.marker.getElement();
        if (el) {
          el.style.visibility = visible ? 'visible' : 'hidden';
          el.style.pointerEvents = visible ? '' : 'none';
        }
      });
      this.eventMarkers.forEach((e) => {
        e.marker.setOpacity(0);
        const el = e.marker.getElement();
        if (el) {
          el.style.visibility = 'hidden';
          el.style.pointerEvents = 'none';
        }
      });
      if (this.merchantMarker) {
        this.merchantMarker.setOpacity(0.3);
        const el = this.merchantMarker.getElement();
        if (el) {
          el.style.visibility = 'visible';
          el.style.pointerEvents = '';
        }
      }
    } else {
      // Pas de filtre : afficher tout selon rayon
      this.eventMarkers.forEach(apply);
      this.oppMarkers.forEach(apply);
      if (this.merchantMarker) {
        this.merchantMarker.setOpacity(1);
        const el = this.merchantMarker.getElement();
        if (el) {
          el.style.visibility = 'visible';
          el.style.pointerEvents = '';
        }
      }
    }
  }

  private updateMerchantPosition(): void {
    if (!this.mapInitialized || !this.merchantLocation()) return;

    const center: L.LatLngExpression = [
      this.merchantLocation()!.lat,
      this.merchantLocation()!.lng,
    ];

    if (this.merchantMarker) {
      this.merchantMarker.setLatLng(center);
    }
    if (this.radiusCircle) {
      this.radiusCircle.setLatLng(center);
      this.radiusCircle.setRadius(this.radiusValue * 1000);
    }
    this.map.setView(center, this.map.getZoom());
  }

  // ═══ Custom icons ═══

  private createDivIcon(emoji: string, bgClass: string): L.DivIcon {
    const L = this._L!;
    return L.divIcon({
      html: `<div class="map-marker ${bgClass}">${emoji}</div>`,
      className: 'custom-map-icon',
      iconSize: [32, 32],
      iconAnchor: [16, 16],
      popupAnchor: [0, -18],
    });
  }

  private createDivIconWithBadge(
    emoji: string,
    bgClass: string,
    count: number,
  ): L.DivIcon {
    const L = this._L!;
    return L.divIcon({
      html: `<div class="map-marker ${bgClass}">${emoji}<span style="position:absolute;top:-6px;right:-6px;background:#dc2626;color:white;border-radius:50%;width:18px;height:18px;display:flex;align-items:center;justify-content:center;font-size:10px;font-weight:700">${count}</span></div>`,
      className: 'custom-map-icon',
      iconSize: [32, 32],
      iconAnchor: [16, 16],
      popupAnchor: [0, -18],
    });
  }

  private createMixedDivIcon(
    eventsCount: number,
    oppsCount: number,
  ): L.DivIcon {
    const L = this._L!;
    return L.divIcon({
      html: `
        <div style="position:relative;width:36px;height:36px">
          <div class="map-marker" style="background:linear-gradient(135deg,#7c3aed 0%,#e11d48 100%);width:36px;height:36px;font-size:16px">📅</div>
          <span style="position:absolute;top:-7px;left:-7px;background:#7c3aed;color:white;border-radius:50%;width:18px;height:18px;display:flex;align-items:center;justify-content:center;font-size:10px;font-weight:700;border:1.5px solid white">${eventsCount}</span>
          <span style="position:absolute;top:-7px;right:-7px;background:#e11d48;color:white;border-radius:50%;width:18px;height:18px;display:flex;align-items:center;justify-content:center;font-size:10px;font-weight:700;border:1.5px solid white">${oppsCount}</span>
        </div>`,
      className: 'custom-map-icon',
      iconSize: [36, 36],
      iconAnchor: [18, 18],
      popupAnchor: [0, -20],
    });
  }

  private getEventEmoji(eventType: string): string {
    const icons: Record<string, string> = {
      festival: '🎪',
      market: '🏪',
      private: '🎉',
      corporate: '🏢',
      school: '🎓',
      other: '📍',
    };
    return icons[eventType] || '📍';
  }

  // ═══ Popups ═══

  private createFollowedEventsPopup(opps: Opportunity[]): string {
    const location = opps[0].fullAddress || opps[0].location;
    const items = opps
      .map(
        (opp) => `
      <div style="border-bottom:1px solid #e5e7eb;padding:8px 0;cursor:pointer" 
             onclick="(function(){var el=document.getElementById('followed-detail-${opp.id}'); if(el){ el.style.transform='translateX(0)'; el.style.opacity='1'; var popup=el.closest('.leaflet-popup'); if(popup){ var tip=popup.querySelector('.leaflet-popup-tip'); if(tip) tip.style.display='none'; } } })();"
           onmouseover="this.style.background='#f9fafb'" onmouseout="this.style.background='white'">
          <div style="display:flex;align-items:start;gap:8px">
          <div style="display:flex;flex-direction:column;align-items:center">
            <span style="font-size:16px">📅</span>
            <span style="width:8px;height:8px;border-radius:50%;background:${opp.profitabilityLevel === 'high' ? '#22c55e' : opp.profitabilityLevel === 'medium' ? '#facc15' : '#f97316'};margin-top:6px"></span>
          </div>
          <div style="flex:1">
            <p style="font-weight:600;margin:0;font-size:13px;color:#111827">${opp.name}</p>
            <p style="margin:2px 0 0;font-size:11px;color:#6b7280">📅 ${opp.dateFormatted || opp.date} ${opp.timeRange ? '· ' + opp.timeRange : ''}</p>
            <p style="margin:2px 0 0;font-size:11px;color:#9333ea;font-weight:600">✓ Planifié</p>
          </div>
        </div>
      </div>
    `,
      )
      .join('');

    const detailPanels = opps
      .map(
        (opp) => `
      <div id="followed-detail-${opp.id}" data-panel="true" style="position:absolute;top:0;left:0;width:100%;background:white;transform:translateX(100%);opacity:0;transition:all 0.3s ease;overflow:visible;border-radius:12px;">
        ${this.createOppPopup(opp, true)}
      </div>
    `,
      )
      .join('');

    return `
      <div style="font-family:system-ui;width:320px;position:relative;overflow:visible;box-sizing:border-box;">
        <div id="followed-list-view">
          <div style="background:#9333ea;color:white;padding:10px;margin:-10px -10px 10px">
            <p style="font-weight:700;margin:0;font-size:14px">📅 ${opps.length} événement${opps.length > 1 ? 's' : ''} planifié${opps.length > 1 ? 's' : ''}</p>
            <p style="font-size:12px;margin:4px 0 0;opacity:0.9">📍 ${location}</p>
          </div>
          <div style="${opps.length > 3 ? 'max-height:240px;overflow-y:auto;padding-right:2px;' : ''}">
            ${items}
          </div>
          <p style="font-size:10px;color:#9ca3af;margin:8px 0 0;text-align:center">💡 Cliquez pour voir les détails</p>
        </div>
        ${detailPanels}
      </div>
    `;
  }

  private createGroupedOppPopup(opps: Opportunity[]): string {
    if (opps.length === 1) {
      return this.createOppPopup(opps[0], false);
    }

    // Plusieurs opportunités au même endroit
    const location = opps[0].fullAddress || opps[0].location;
    const sortedOpps = opps.sort((a, b) => {
      const levelOrder = { high: 0, medium: 1, low: 2 };
      const aLevel =
        levelOrder[a.profitabilityLevel as keyof typeof levelOrder] ?? 2;
      const bLevel =
        levelOrder[b.profitabilityLevel as keyof typeof levelOrder] ?? 2;
      return aLevel - bLevel;
    });

    const items = sortedOpps
      .map((opp) => {
        const isFollowed = this.followedOpportunityIds.has(opp.id);
        const levelColors: Record<string, { dot: string; text: string }> = {
          high: { dot: '#22c55e', text: '#16a34a' },
          medium: { dot: '#facc15', text: '#ca8a04' },
          low: { dot: '#f97316', text: '#ea580c' },
        };
        const colors =
          levelColors[opp.profitabilityLevel] || levelColors['low'];

        return `
        <div style="position:relative;border:1px solid ${isFollowed ? '#d1d5db' : '#e5e7eb'};border-radius:6px;padding:8px;margin-bottom:8px;cursor:pointer;transition:all 0.2s;background:${isFollowed ? '#f3f4f6' : 'white'};opacity:${isFollowed ? '0.7' : '1'}" 
             onclick="(function(){var el=document.getElementById('opp-detail-${opp.id}'); if(el){ el.style.transform='translateX(0)'; el.style.opacity='1'; var popup=el.closest('.leaflet-popup'); if(popup){ var tip=popup.querySelector('.leaflet-popup-tip'); if(tip) tip.style.display='none'; } } })();"
             onmouseover="this.style.background='${isFollowed ? '#e5e7eb' : '#f9fafb'}'" onmouseout="this.style.background='${isFollowed ? '#f3f4f6' : 'white'}'">
          ${isFollowed ? '<div style="position:absolute;top:4px;right:4px;width:16px;height:16px;border-radius:50%;background:#9333ea;display:flex;align-items:center;justify-content:center;font-size:10px;">✓</div>' : ''}
          ${isFollowed ? '<div style="position:absolute;top:50%;left:50%;transform:translate(-50%,-50%) rotate(-15deg);background:#9333ea;color:white;padding:4px 12px;border-radius:4px;font-size:10px;font-weight:700;opacity:0.9;pointer-events:none;">PLANIFIÉ</div>' : ''}
          <div style="display:flex;align-items:start;gap:8px;margin-bottom:4px">
            <span style="width:8px;height:8px;border-radius:50%;background:${colors.dot};flex-shrink:0;margin-top:4px"></span>
            <div style="flex:1">
              <p style="font-weight:600;margin:0;font-size:13px;color:${isFollowed ? '#6b7280' : '#111827'}">${opp.name}</p>
              <p style="margin:2px 0 0;font-size:11px;color:#6b7280">📅 ${opp.dateFormatted || opp.date} ${opp.timeRange ? '· ' + opp.timeRange : ''}</p>
            </div>
          </div>
          <div style="display:flex;gap:8px;font-size:11px;color:#6b7280">
            <span>👥 ${opp.expectedAttendance || '?'}</span>
            <span style="color:${isFollowed ? '#6b7280' : colors.text};font-weight:600">💰 ${opp.estimatedRevenue.low}–${opp.estimatedRevenue.high}€</span>
          </div>
        </div>
      `;
      })
      .join('');

    const detailPanels = sortedOpps
      .map(
        (opp) => `
      <div id="opp-detail-${opp.id}" data-panel="true" style="position:absolute;top:0;left:0;width:100%;background:white;transform:translateX(100%);opacity:0;transition:all 0.3s ease;overflow:visible;border-radius:12px;">
        ${this.createOppPopup(opp, true)}
      </div>
    `,
      )
      .join('');

    return `
      <div style="font-family:system-ui;width:320px;position:relative;overflow:visible;box-sizing:border-box;">
        <div id="opp-list-view">
          <div style="background:#f9fafb;border-bottom:1px solid #e5e7eb;padding:10px;margin:-10px -10px 10px">
            <p style="font-weight:700;margin:0;font-size:14px;color:#111827">📍 ${opps.length} opportunités</p>
            <p style="font-size:12px;color:#6b7280;margin:4px 0 0">${location}</p>
          </div>
          <div style="${opps.length > 3 ? 'max-height:240px;overflow-y:auto;padding-right:2px;' : ''}">
            ${items}
          </div>
          <p style="font-size:10px;color:#9ca3af;margin:8px 0 0;text-align:center">💡 Cliquez sur une opportunité pour plus de détails</p>
        </div>
        ${detailPanels}
      </div>
    `;
  }

  private createMerchantPopup(): string {
    if (!this.merchantLocation()) return '';
    return `
      <div style="font-family:system-ui;min-width:140px">
        <p style="font-weight:700;margin:0 0 4px">🏠 Ma position</p>
        <p style="font-size:12px;color:#6b7280;margin:0">${this.merchantLocation()!.address}</p>
        <p style="font-size:12px;color:#6b7280;margin:2px 0 0">${this.merchantLocation()!.city}</p>
        <p style="font-size:11px;color:#6366f1;margin:4px 0 0;font-weight:600">Rayon : ${this.radiusValue} km</p>
      </div>
    `;
  }

  private createEventPopup(event: MapEvent): string {
    const statusLabel: Record<string, string> = {
      planned: '📅 Planifié',
      active: '🟢 En cours',
      completed: '✅ Terminé',
      analyzed: '📊 Analysé',
    };
    const dotColor =
      event.status === 'active'
        ? '#22c55e'
        : event.status === 'completed'
          ? '#6b7280'
          : '#9333ea';

    return `
      <div style="font-family:system-ui;width:320px;box-sizing:border-box;padding:8px 12px">
        <div style="display:flex;align-items:center;gap:8px;margin-bottom:6px">
          <span style="width:10px;height:10px;border-radius:50%;background:${dotColor};flex-shrink:0"></span>
          <p style="font-weight:700;margin:0;font-size:14px">${this.getEventEmoji(event.eventType)} ${event.name}</p>
        </div>
        <p style="font-size:12px;color:#6b7280;margin:0">📍 ${event.location}</p>
        <p style="font-size:11px;margin:4px 0 0">${statusLabel[event.status] || event.status}</p>
      </div>
    `;
  }

  private createOppPopup(
    opp: Opportunity,
    showBackButton: boolean = false,
  ): string {
    const matchColor =
      opp.activityMatch >= 80
        ? '#16a34a'
        : opp.activityMatch >= 60
          ? '#2563eb'
          : '#6b7280';

    // Couleurs basées sur profitabilityLevel
    const levelColors: Record<
      string,
      { dot: string; bg: string; border: string; text: string }
    > = {
      high: {
        dot: '#22c55e',
        bg: '#f0fdf4',
        border: '#86efac',
        text: '#16a34a',
      }, // vert
      medium: {
        dot: '#facc15',
        bg: '#fefce8',
        border: '#fde047',
        text: '#ca8a04',
      }, // jaune
      low: {
        dot: '#f97316',
        bg: '#fff7ed',
        border: '#fdba74',
        text: '#ea580c',
      }, // orange
    };
    const colors = levelColors[opp.profitabilityLevel] || levelColors['low'];

    const backButton = showBackButton
      ? `<button onclick="(function(btn){var p=btn.closest('[data-panel]');if(p){p.style.transform='translateX(100%)';p.style.opacity='0';}})(this)"
              style="width:100%;padding:8px;border:1px solid #e5e7eb;border-radius:6px;background:#f9fafb;cursor:pointer;font-size:12px;color:#374151;margin-top:8px;font-family:system-ui">← Retour à la liste</button>`
      : '';

    const isFollowed = this.followedOpportunityIds.has(opp.id);
    const followButton = isFollowed
      ? `<div style="flex:1;background:#9333ea;color:white;border:none;padding:8px 12px;border-radius:6px;font-weight:600;font-size:12px;text-align:center">✓ Déjà planifié</div>`
      : `<button onclick="window.dispatchEvent(new CustomEvent('followOpportunity', {detail:'${opp.id}'}))" 
                  style="flex:1;background:${opp.profitabilityLevel === 'high' ? '#2563eb' : opp.profitabilityLevel === 'medium' ? '#ca8a04' : '#9ca3af'};color:white;border:none;padding:8px 12px;border-radius:6px;cursor:pointer;font-weight:600;font-size:12px">
            ${opp.profitabilityLevel === 'high' ? '➕ Suivre' : opp.profitabilityLevel === 'medium' ? '👁️ À surveiller' : '⏸️ Peu rentable'}
          </button>`;

    return `
      <div style="font-family:system-ui;width:320px;position:relative;padding:12px;box-sizing:border-box;">
        ${isFollowed ? '<div style="position:absolute;top:8px;right:8px;background:#9333ea;color:white;padding:4px 8px;border-radius:6px;font-size:10px;font-weight:700;display:flex;align-items:center;gap:4px;z-index:10"><span>✓</span> PLANIFIÉ</div>' : ''}
        <div style="display:flex;align-items:center;gap:8px;margin-bottom:6px">
          <span style="width:10px;height:10px;border-radius:50%;background:${colors.dot};flex-shrink:0"></span>
          <p style="font-weight:700;margin:0;font-size:14px">${opp.eventTypeLabel || '📅'} ${opp.name}</p>
        </div>
        
        <div style="font-size:12px;color:#6b7280;margin-bottom:8px">
          <p style="margin:2px 0">📍 ${opp.fullAddress || opp.location}</p>
          <p style="margin:2px 0">📅 ${opp.dateFormatted || opp.date} ${opp.timeRange ? '· ' + opp.timeRange : ''}</p>
          <p style="margin:2px 0">👥 <strong>${opp.expectedAttendance || '?'}</strong> visiteurs attendus</p>
        </div>
        
        <div style="display:flex;gap:12px;margin-bottom:8px;font-size:12px">
          <span style="font-weight:600;color:#2563eb">Score: ${opp.score}/100</span>
          <span style="color:${matchColor}">${opp.activityMatchLabel || ''}</span>
        </div>
        
        ${
          opp.profitabilityLevel === 'low'
            ? `
        <div style="background:#fff7ed;border:1px solid #fdba74;border-radius:6px;padding:6px 8px;margin-bottom:8px;font-size:11px;color:#c2410c">
          ⚠️ Rentabilité limitée — Min conseillé: ${opp.minViableCA}€
        </div>
        `
            : ''
        }
        
        <div style="background:${colors.bg};border:1px solid ${colors.border};border-radius:6px;padding:10px;margin-bottom:8px">
          <div style="display:flex;justify-content:space-between;margin-bottom:6px;font-size:13px">
            <span>💰 CA estimé</span>
            <span style="font-weight:700;color:${colors.text}">${opp.estimatedRevenue.low}–${opp.estimatedRevenue.high}€</span>
          </div>
          <div style="display:flex;justify-content:space-between;margin-bottom:6px;font-size:13px">
            <span>📈 Marge nette</span>
            <span style="font-weight:700;color:${colors.text}">${opp.estimatedMargin?.low || 0}–${opp.estimatedMargin?.high || 0}€</span>
          </div>
          <div style="display:flex;justify-content:space-between;font-size:12px;color:#6b7280">
            <span>🛒 ~${opp.expectedCustomers || 0} clients</span>
            <span>⏱️ Charge ${opp.capacityUsed || 0}%</span>
          </div>
        </div>
        
        <div style="display:flex;gap:8px">
          ${followButton}
          ${opp.url ? `<a href="${opp.url}" target="_blank" style="padding:8px 12px;border:1px solid #e5e7eb;border-radius:6px;text-decoration:none;color:#374151;font-size:12px">🔗</a>` : ''}
        </div>
        ${backButton}
      </div>
    `;
  }

  // ═══ Radius management ═══

  isInRadius(lat: number, lng: number): boolean {
    if (!this.merchantLocation()) return true;
    return this.calcDistM(lat, lng) <= this.radiusValue * 1000;
  }

  onRadiusChange(): void {
    // Mettre à jour le cercle Leaflet
    if (this.radiusCircle) {
      this.radiusCircle.setRadius(this.radiusValue * 1000);
    }
    // Filtrage synchrone : distance pré-stockée = comparée directement, pas de recalcul
    this.applyRadiusFilter(this.radiusValue);
    this.radiusChanged.emit(this.radiusValue);
  }

  toggleExpand(): void {
    this.isExpanded = !this.isExpanded;
    setTimeout(() => this.map?.invalidateSize(), 300);
  }

  toggleFullscreen(): void {
    this.isFullscreen = !this.isFullscreen;
    // Bloquer le scroll du body en fullscreen
    document.body.style.overflow = this.isFullscreen ? 'hidden' : '';
    setTimeout(() => this.map?.invalidateSize(), 100);
  }

  /** Fermer fullscreen avec Escape */
  @HostListener('document:keydown.escape')
  onEscapeFullscreen(): void {
    if (this.isFullscreen) {
      this.toggleFullscreen();
    }
  }

  /** Quitter fullscreen et naviguer vers le dashboard */
  goToDashboard(): void {
    if (this.isFullscreen) {
      this.toggleFullscreen();
    }
    this.router.navigate(['/dashboard']);
  }

  centerOnMerchant(): void {
    if (!this.merchantLocation() || !this.map) return;
    if (this.radiusCircle) {
      this.map.fitBounds(this.radiusCircle.getBounds(), { padding: [20, 20] });
    } else {
      this.map.setView(
        [this.merchantLocation()!.lat, this.merchantLocation()!.lng],
        12,
      );
    }
  }

  onLegendMerchantClick(): void {
    if (this.activeLegendFilter === 'merchant') {
      // Désactiver le filtre
      this.activeLegendFilter = null;
    } else {
      // Activer le filtre marchand
      this.activeLegendFilter = 'merchant';
      if (this.map && this.merchantLocation()) {
        this.map.setView(
          [this.merchantLocation()!.lat, this.merchantLocation()!.lng],
          13,
        );
        // Ne pas ouvrir automatiquement le popup
      }
    }
    this.applyRadiusFilter();
  }

  onLegendEventsClick(): void {
    if (this.activeLegendFilter === 'events') {
      // Désactiver le filtre
      this.activeLegendFilter = null;
    } else {
      // Activer le filtre événements
      this.activeLegendFilter = 'events';
      if (this.map) {
        const visibleMarkers = this.eventMarkers
          .filter((e) => e.distM <= this.radiusValue * 1000)
          .map((e) => e.marker);
        this.focusOnMarkers(visibleMarkers);
      }
    }
    this.applyRadiusFilter();
  }

  onLegendOpportunitiesHighClick(): void {
    if (this.activeLegendFilter === 'opp-high') {
      // Désactiver le filtre
      this.activeLegendFilter = null;
    } else {
      // Activer le filtre opportunités high
      this.activeLegendFilter = 'opp-high';
      if (this.map) {
        const visibleMarkers = this.oppMarkers
          .filter(
            (o) => o.distM <= this.radiusValue * 1000 && o.level === 'high',
          )
          .map((o) => o.marker);
        this.focusOnMarkers(visibleMarkers);
      }
    }
    this.applyRadiusFilter();
  }

  onLegendOpportunitiesMediumClick(): void {
    if (this.activeLegendFilter === 'opp-medium') {
      // Désactiver le filtre
      this.activeLegendFilter = null;
    } else {
      // Activer le filtre opportunités medium
      this.activeLegendFilter = 'opp-medium';
      if (this.map) {
        const visibleMarkers = this.oppMarkers
          .filter(
            (o) => o.distM <= this.radiusValue * 1000 && o.level === 'medium',
          )
          .map((o) => o.marker);
        this.focusOnMarkers(visibleMarkers);
      }
    }
    this.applyRadiusFilter();
  }

  onLegendOpportunitiesLowClick(): void {
    if (this.activeLegendFilter === 'opp-low') {
      // Désactiver le filtre
      this.activeLegendFilter = null;
    } else {
      // Activer le filtre opportunités low
      this.activeLegendFilter = 'opp-low';
      if (this.map) {
        const visibleMarkers = this.oppMarkers
          .filter(
            (o) => o.distM <= this.radiusValue * 1000 && o.level === 'low',
          )
          .map((o) => o.marker);
        this.focusOnMarkers(visibleMarkers);
      }
    }
    this.applyRadiusFilter();
  }

  private focusOnMarkers(markers: L.Marker[]): void {
    if (!this.map || markers.length === 0) return;
    const L = this._L!;

    if (markers.length === 1) {
      const latLng = markers[0].getLatLng();
      this.map.setView(
        [latLng.lat, latLng.lng],
        Math.max(this.map.getZoom(), 13),
      );
      markers[0].openPopup();
      return;
    }

    const group = L.featureGroup(markers);
    this.map.fitBounds(group.getBounds(), { padding: [24, 24] });
  }
}
